import React, { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { db, auth } from "../../firebase";
import {
  collection, query, where, getDocs, deleteDoc,
  doc, getDoc, setDoc, orderBy, Timestamp
} from "firebase/firestore";
import { useAuthState } from "react-firebase-hooks/auth";
import ScheduleNewGame from "./ScheduleNewGame";
import styles from "./SchedulePage.module.css"; // Import the CSS module

function SchedulePage() {
  const { teamId } = useParams();
  const [user] = useAuthState(auth);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [canManageSchedule, setCanManageSchedule] = useState(false);
  const [gameToEdit, setGameToEdit] = useState(null);
  const [myAvailability, setMyAvailability] = useState({});
  const [teamAvailability, setTeamAvailability] = useState({});
  const [activeFilter, setActiveFilter] = useState("upcoming");
  const [showForm, setShowForm] = useState(false);
  const [teamInfo, setTeamInfo] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());

  // Fetch games from Firestore - updated with more robust error handling
  useEffect(() => {
    const fetchGames = async () => {
      try {
        setLoading(true);
        console.log("Fetching games for teamId:", teamId);
        
        let allGames = [];
        
        // Try the new data model first (with teamId)
        try {
          const newModelQuery = query(
            collection(db, "games"),
            where("teamId", "==", teamId)
          );
          
          const newModelSnap = await getDocs(newModelQuery);
          console.log("New model query results:", newModelSnap.size);
          
          if (!newModelSnap.empty) {
            // Use new data model games
            allGames = newModelSnap.docs.map(d => {
              const data = d.data();
              return { 
                id: d.id, 
                ...data, 
                // Always set isHomeTeam to true for display purposes with new model
                isHomeTeam: true
              };
            });
            console.log("Found games with new model:", allGames.length);
          }
        } catch (newModelErr) {
          console.error("Error fetching games with new model:", newModelErr);
        }
        
        // If no games found with new model, try the old model
        if (allGames.length === 0) {
          console.log("No games found with new model, trying old model");
          
          // Try home games
          try {
            const homeGamesQuery = query(
              collection(db, "games"), 
              where("homeTeamId", "==", teamId)
            );
            
            const homeGamesSnap = await getDocs(homeGamesQuery);
            console.log("Home games found:", homeGamesSnap.size);
            
            const homeGames = homeGamesSnap.docs.map(d => ({
              id: d.id,
              ...d.data(),
              isHomeTeam: true
            }));
            
            allGames = [...homeGames];
          } catch (homeErr) {
            console.error("Error fetching home games:", homeErr);
          }
          
          // Try away games
          try {
            const awayGamesQuery = query(
              collection(db, "games"), 
              where("awayTeamId", "==", teamId)
            );
            
            const awayGamesSnap = await getDocs(awayGamesQuery);
            console.log("Away games found:", awayGamesSnap.size);
            
            const awayGames = awayGamesSnap.docs.map(d => ({
              id: d.id,
              ...d.data(),
              isHomeTeam: false
            }));
            
            allGames = [...allGames, ...awayGames];
          } catch (awayErr) {
            console.error("Error fetching away games:", awayErr);
          }
        }
        
        console.log("Total games collected:", allGames.length);
        
        // Sort all games by date
        allGames.sort((a, b) => new Date(a.date) - new Date(b.date));
        setGames(allGames);
      } catch (err) {
        console.error("Error fetching games:", err);
        setError("Failed to load schedule: " + (err.message || "Unknown error"));
      } finally {
        setLoading(false);
      }
    };
    
    if (teamId) {
      fetchGames();
    } else {
      console.error("No teamId provided");
      setError("No team ID found. Please check the URL.");
      setLoading(false);
    }
  }, [teamId]);

  // Fetch team info
  useEffect(() => {
    const fetchTeamInfo = async () => {
      if (!teamId) {
        console.error("No teamId for team info fetch");
        return;
      }
      
      try {
        console.log("Fetching team info for teamId:", teamId);
        const teamRef = doc(db, "teams", teamId);
        const teamSnap = await getDoc(teamRef);
        
        if (teamSnap.exists()) {
          const teamData = teamSnap.data();
          console.log("Team info found:", teamData.name || "Unknown team");
          setTeamInfo(teamData);
          setCanManageSchedule(teamData.coachId === user?.uid);
        } else {
          console.error("Team document not found");
        }
      } catch (err) {
        console.error("Error fetching team info:", err);
      }
    };
    
    if (teamId && user) {
      fetchTeamInfo();
    }
  }, [teamId, user]);

  // Fetch team availability for all games - chunked for Firebase limits
  useEffect(() => {
    const fetchTeamAvailability = async () => {
      if (!games.length) return;
      
      try {
        console.log("Fetching team availability for", games.length, "games");
        const availabilityMap = {};
        
        // Firebase has a limit of 10 items in an "in" query
        // So we need to chunk our game IDs
        const gameIds = games.map(game => game.id);
        const chunkSize = 10;
        
        // Split gameIds into chunks of 10
        for (let i = 0; i < gameIds.length; i += chunkSize) {
          const chunk = gameIds.slice(i, i + chunkSize);
          console.log(`Fetching availability for chunk ${i / chunkSize + 1}`, chunk.length, "games");
          
          try {
            const q = query(
              collection(db, "gameAvailability"),
              where("teamId", "==", teamId),
              where("gameId", "in", chunk)
            );
            
            const snap = await getDocs(q);
            console.log("Availability entries found in chunk:", snap.size);
            
            snap.docs.forEach(doc => {
              const data = doc.data();
              if (!availabilityMap[data.gameId]) {
                availabilityMap[data.gameId] = {
                  available: 0,
                  unavailable: 0,
                  playerIds: {}
                };
              }
              
              availabilityMap[data.gameId].playerIds[data.playerId] = data.availability;
              availabilityMap[data.gameId][data.availability]++;
            });
          } catch (chunkErr) {
            console.error(`Error fetching chunk ${i / chunkSize + 1}:`, chunkErr);
          }
        }
        
        setTeamAvailability(availabilityMap);
      } catch (err) {
        console.error("Error fetching team availability:", err);
      }
    };
    
    fetchTeamAvailability();
  }, [games, teamId]);

  // Fetch current user's availability
  useEffect(() => {
    if (!user || !teamId) return;
    
    const fetchMyAvailability = async () => {
      try {
        console.log("Fetching my availability for user:", user.uid);
        const q = query(
          collection(db, "gameAvailability"),
          where("teamId", "==", teamId),
          where("playerId", "==", user.uid)
        );
        
        const snap = await getDocs(q);
        console.log("My availability entries found:", snap.size);
        
        const availMap = {};
        snap.docs.forEach(docSnap => {
          const data = docSnap.data();
          availMap[data.gameId] = data.availability;
        });
        
        setMyAvailability(availMap);
      } catch (err) {
        console.error("Error fetching availability:", err);
      }
    };
    
    fetchMyAvailability();
  }, [user, teamId]);

  // Handler for game saved
  const handleGameSaved = (savedGame) => {
    console.log("Game saved:", savedGame);
    setGames(prevGames => {
      // Check if the game is already in the list
      const existing = prevGames.find(g => g.id === savedGame.id);
      if (existing) {
        // If editing an existing game, update it
        return prevGames
          .map(g => g.id === savedGame.id ? { ...savedGame, isHomeTeam: true } : g)
          .sort((a, b) => new Date(a.date) - new Date(b.date));
      } else {
        // If adding a new game, add it with isHomeTeam: true for display consistency
        return [...prevGames, { ...savedGame, isHomeTeam: true }]
          .sort((a, b) => new Date(a.date) - new Date(b.date));
      }
    });
    setGameToEdit(null);
    setShowForm(false);
  };

  // Handler for editing a game
  const handleEditClick = (game) => {
    console.log("Editing game:", game.id);
    setGameToEdit(game);
    setShowForm(true);
    // Scroll to form
    setTimeout(() => {
      document.getElementById("game-form")?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  // Handler for game removal
  const handleRemoveGame = async (gameId) => {
    if (!canManageSchedule) {
      console.log("User cannot manage schedule");
      return;
    }
    
    // Use window.confirm instead of global confirm
    if (!window.confirm("Are you sure you want to remove this game?")) return;
    
    try {
      console.log("Removing game:", gameId);
      await deleteDoc(doc(db, "games", gameId));
      setGames(games.filter(game => game.id !== gameId));
      console.log("Game removed successfully");
    } catch (err) {
      console.error("Game deletion failed:", err);
      setError("Failed to remove game: " + (err.message || "Unknown error"));
    }
  };

  // Handler for setting availability
  const handleAvailability = async (gameId, availability) => {
    if (!user) {
      console.log("No user logged in");
      return;
    }
    
    try {
      console.log("Setting availability for game:", gameId, "to", availability);
      const availabilityDocRef = doc(db, "gameAvailability", `${gameId}_${user.uid}`);
      
      await setDoc(availabilityDocRef, {
        teamId,
        gameId,
        playerId: user.uid,
        availability,
        updatedAt: Timestamp.now(),
      });
      
      console.log("Availability updated successfully");
      
      // Update local state
      setMyAvailability(prev => ({ ...prev, [gameId]: availability }));
      
      // Update team availability counts
      setTeamAvailability(prev => {
        const gameAvail = prev[gameId] || { available: 0, unavailable: 0, playerIds: {} };
        const oldStatus = gameAvail.playerIds[user.uid];
        
        // Decrement old status count if it existed
        if (oldStatus) {
          gameAvail[oldStatus]--;
        }
        
        // Increment new status count
        gameAvail[availability]++;
        gameAvail.playerIds[user.uid] = availability;
        
        return { ...prev, [gameId]: gameAvail };
      });
    } catch (err) {
      console.error("Error updating availability:", err);
      alert("Failed to set availability. Please try again.");
    }
  };

  // Update game status
  const handleUpdateStatus = async (gameId, newStatus) => {
    if (!canManageSchedule) {
      console.log("User cannot manage schedule");
      return;
    }
    
    try {
      console.log("Updating game status:", gameId, "to", newStatus);
      const gameRef = doc(db, "games", gameId);
      
      await setDoc(gameRef, {
        status: newStatus,
        lastUpdated: Timestamp.now()
      }, { merge: true });
      
      console.log("Game status updated successfully");
      
      setGames(prevGames => 
        prevGames.map(game => 
          game.id === gameId ? { ...game, status: newStatus } : game
        )
      );
    } catch (err) {
      console.error("Error updating game status:", err);
      alert("Failed to update game status: " + (err.message || "Unknown error"));
    }
  };

  // Helper function to get appropriate scores from game object (works with both data models)
  const getScores = (game) => {
    // If using the new model with yourScore/opponentScore
    if (game.yourScore !== undefined) {
      return {
        yourScore: game.yourScore,
        opponentScore: game.opponentScore
      };
    }
    
    // Otherwise use old model with home/away scores
    return {
      yourScore: game.isHomeTeam ? game.homeScore : game.awayScore,
      opponentScore: game.isHomeTeam ? game.awayScore : game.homeScore
    };
  };

  // Filter games based on active filter
  const filteredGames = useMemo(() => {
    const now = new Date();
    
    if (activeFilter === "upcoming") {
      return games.filter(game => new Date(game.date) >= now);
    } else if (activeFilter === "past") {
      return games.filter(game => new Date(game.date) < now);
    }
    return games;
  }, [games, activeFilter]);

  // Games organized by month for calendar view
  const gamesByMonth = useMemo(() => {
    const grouped = {};
    
    games.forEach(game => {
      const date = new Date(game.date);
      const monthKey = `${date.getFullYear()}-${date.getMonth()}`;
      
      if (!grouped[monthKey]) {
        grouped[monthKey] = [];
      }
      
      grouped[monthKey].push(game);
    });
    
    return grouped;
  }, [games]);

  // Generate calendar days for current month
  const calendarDays = useMemo(() => {
    const days = [];
    const firstDay = new Date(currentYear, currentMonth, 1);
    const lastDay = new Date(currentYear, currentMonth + 1, 0);
    const monthKey = `${currentYear}-${currentMonth}`;
    const monthGames = gamesByMonth[monthKey] || [];
    
    // Add empty slots for days before the first day of month
    for (let i = 0; i < firstDay.getDay(); i++) {
      days.push({ day: null, games: [] });
    }
    
    // Add days of the month with their games
    for (let day = 1; day <= lastDay.getDate(); day++) {
      const dayDate = new Date(currentYear, currentMonth, day);
      const dayGames = monthGames.filter(game => {
        const gameDate = new Date(game.date);
        return gameDate.getDate() === day;
      });
      
      days.push({ day, games: dayGames });
    }
    
    return days;
  }, [currentYear, currentMonth, gamesByMonth]);

  // Format date for better readability
  const formatGameDate = (dateString) => {
    const date = new Date(dateString);
    const options = { weekday: 'short', month: 'short', day: 'numeric' };
    return date.toLocaleDateString(undefined, options);
  };

  // Get month name
  const getMonthName = (month) => {
    return new Date(2000, month, 1).toLocaleString('default', { month: 'long' });
  };

  // Navigate to previous month
  const goToPrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  // Navigate to next month
  const goToNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  // Determine home/away display for games
  const getHomeAwayText = (game) => {
    // If it has the old model fields
    if (game.homeTeamId || game.awayTeamId) {
      return game.isHomeTeam ? "vs" : "at";
    }
    
    // For new model, default to "vs" since we don't track home/away anymore
    return "vs";
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner}></div>
        <div className={styles.loadingText}>Loading schedule...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorMessage}>
        <div className={styles.errorTitle}>Error</div>
        {error}
      </div>
    );
  }

  return (
    <div className={styles.scheduleContainer}>
      <header className={styles.scheduleHeader}>
        <div className={styles.headerTop}>
          <h1 className={styles.teamTitle}>
            {teamInfo?.name || "Team"} Schedule
          </h1>
          
          {canManageSchedule && (
            <button
              onClick={() => {
                setGameToEdit(null);
                setShowForm(!showForm);
                if (!showForm) {
                  // Add setTimeout to ensure the form element exists before scrolling
                  setTimeout(() => {
                    document.getElementById("game-form")?.scrollIntoView({ behavior: "smooth" });
                  }, 100);
                }
              }}
              className={styles.addGameButton}
            >
              {showForm ? "Cancel" : "Add Game"}
            </button>
          )}
        </div>
        
        {/* View toggle - List vs Calendar */}
        <div className={styles.viewToggle}>
          <div className={styles.toggleContainer}>
            <button
              className={`${styles.toggleButton} ${activeFilter !== "calendar" ? styles.active : ""}`}
              onClick={() => setActiveFilter("upcoming")}
            >
              List View
            </button>
            <button
              className={`${styles.toggleButton} ${activeFilter === "calendar" ? styles.active : ""}`}
              onClick={() => setActiveFilter("calendar")}
            >
              Calendar
            </button>
          </div>
        </div>
        
        {/* List filter tabs */}
        {activeFilter !== "calendar" && (
          <div className={styles.filterTabs}>
            <button
              className={`${styles.filterTab} ${activeFilter === "upcoming" ? styles.active : ""}`}
              onClick={() => setActiveFilter("upcoming")}
            >
              Upcoming Games
            </button>
            <button
              className={`${styles.filterTab} ${activeFilter === "past" ? styles.active : ""}`}
              onClick={() => setActiveFilter("past")}
            >
              Past Games
            </button>
            <button
              className={`${styles.filterTab} ${activeFilter === "all" ? styles.active : ""}`}
              onClick={() => setActiveFilter("all")}
            >
              All Games
            </button>
          </div>
        )}
      </header>

      {/* Calendar view */}
      {activeFilter === "calendar" && (
        <section className={styles.calendarSection}>
          <div className={styles.calendarContainer}>
            {/* Calendar header */}
            <div className={styles.calendarHeader}>
              <button 
                onClick={goToPrevMonth}
                className={styles.calendarNavButton}
              >
                <span>←</span>
              </button>
              
              <h2 className={styles.calendarTitle}>
                {getMonthName(currentMonth)} {currentYear}
              </h2>
              
              <button 
                onClick={goToNextMonth}
                className={styles.calendarNavButton}
              >
                <span>→</span>
              </button>
            </div>
            
            {/* Calendar grid */}
            <div className={styles.calendarDaysHeader}>
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className={styles.dayName}>
                  {day}
                </div>
              ))}
            </div>
            
            <div className={styles.calendarGrid}>
              {calendarDays.map((dayData, index) => (
                <div 
                  key={index} 
                  className={dayData.day ? styles.calendarDay : `${styles.calendarDay} ${styles.emptyDay}`}
                >
                  {dayData.day && (
                    <>
                      <div className={styles.dayNumber}>
                        {dayData.day}
                      </div>
                      
                      {dayData.games.map(game => (
                        <div 
                          key={game.id}
                          className={`${styles.calendarGame} ${styles.homeGame}`}
                          onClick={() => handleEditClick(game)}
                        >
                          {game.time} - {getHomeAwayText(game)} {game.opponentName}
                        </div>
                      ))}
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Games list */}
      {activeFilter !== "calendar" && (
        <section className={styles.gamesListSection}>
          {filteredGames.length === 0 ? (
            <div className={styles.noGamesMessage}>
              <div className={styles.noGamesIcon}>
                <span>📅</span>
              </div>
              <p className={styles.noGamesText}>
                No {activeFilter === "upcoming" ? "upcoming" : activeFilter === "past" ? "past" : ""} games scheduled
              </p>
              {canManageSchedule && !showForm && (
                <button
                  onClick={() => {
                    setGameToEdit(null);
                    setShowForm(true);
                    // Add setTimeout to ensure the form element exists before scrolling
                    setTimeout(() => {
                      document.getElementById("game-form")?.scrollIntoView({ behavior: "smooth" });
                    }, 100);
                  }}
                  className={styles.scheduleGameButton}
                >
                  Schedule a Game
                </button>
              )}
            </div>
          ) : (
            <div className={styles.gamesGrid}>
              {filteredGames.map((game) => {
                // Get game header class based on status
                let gameHeaderClass = styles.gameHeader;
                if (game.status === "completed") {
                  gameHeaderClass = `${styles.gameHeader} ${styles.statusCompleted}`;
                } else if (game.status === "in-progress") {
                  gameHeaderClass = `${styles.gameHeader} ${styles.statusInProgress}`;
                }
                
                return (
                  <div
                    key={game.id}
                    className={styles.gameCard}
                  >
                    <div className={gameHeaderClass}>
                      <div className={styles.gameInfo}>
                        <div className={styles.gameDate}>
                          {formatGameDate(game.date)} • {game.time}
                        </div>
                        <h3 className={styles.opponentName}>
                          <span>{getHomeAwayText(game)} <span className={styles.opponent}>{game.opponentName}</span></span>
                        </h3>
                        {game.notes && (
                          <div className={styles.gameNotes}>{game.notes}</div>
                        )}
                      </div>
                      {canManageSchedule && (
                        <div className={styles.statusControls}>
                          {game.status === "completed" ? (
                            <span className={`${styles.statusBadge} ${styles.statusCompleted}`}>Completed</span>
                          ) : game.status === "in-progress" ? (
                            <span className={`${styles.statusBadge} ${styles.statusInProgress}`}>In Progress</span>
                          ) : (
                            <span className={`${styles.statusBadge} ${styles.statusScheduled}`}>Scheduled</span>
                          )}
                          
                          {game.status !== "completed" && (
                            <div className={styles.statusButtons}>
                              <button
                                onClick={() => handleUpdateStatus(game.id, "in-progress")}
                                className={`${styles.statusButton} ${game.status === "in-progress" ? styles.active : ""}`}
                              >
                                In Progress
                              </button>
                              <button
                                onClick={() => handleUpdateStatus(game.id, "completed")}
                                className={`${styles.statusButton} ${game.status === "completed" ? styles.active : ""}`}
                              >
                                Complete
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                      {!canManageSchedule && (
                        <div>
                          {game.status === "completed" ? (
                            <span className={`${styles.statusBadge} ${styles.statusCompleted}`}>Completed</span>
                          ) : game.status === "in-progress" ? (
                            <span className={`${styles.statusBadge} ${styles.statusInProgress}`}>In Progress</span>
                          ) : (
                            <span className={`${styles.statusBadge} ${styles.statusScheduled}`}>Scheduled</span>
                          )}
                        </div>
                      )}
                    </div>
                    
                    <div className={styles.gameDetails}>
                      <div className={styles.detailsGrid}>
                        {/* Location section */}
                        <div className={styles.detailItem}>
                          <div className={styles.detailIcon}>📍</div>
                          <div className={styles.detailContent}>
                            <div className={styles.detailTitle}>{game.locationName}</div>
                            {game.locationAddress && (
                              <div className={styles.detailSubtitle}>{game.locationAddress}</div>
                            )}
                          </div>
                        </div>
                        
                        {/* Time section */}
                        <div className={styles.detailItem}>
                          <div className={styles.detailIcon}>🕒</div>
                          <div className={styles.detailContent}>
                            <div className={styles.detailTitle}>Game Time</div>
                            <div className={styles.detailSubtitle}>{game.time}</div>
                          </div>
                        </div>
                        
                        {/* Availability section */}
                        <div className={styles.detailItem}>
                          <div className={styles.detailIcon}>👥</div>
                          <div className={styles.detailContent}>
                            <div className={styles.detailTitle}>Team Availability</div>
                            <div className={styles.detailSubtitle}>
                              {teamAvailability[game.id] ? (
                                <span>
                                  <span className={styles.availableCount}>{teamAvailability[game.id].available || 0}</span> available, 
                                  <span className={styles.unavailableCount}> {teamAvailability[game.id].unavailable || 0}</span> unavailable
                                </span>
                              ) : (
                                <span>No responses yet</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Availability buttons */}
                      {game.status !== "completed" && (
                        <div className={styles.availabilityButtons}>
                          <button
                            onClick={() => handleAvailability(game.id, "available")}
                            className={`${styles.availabilityButton} ${myAvailability[game.id] === "available" ? styles.availableActive : ""}`}
                          >
                            <span className={styles.buttonIcon}>✓</span> I Can Play
                          </button>
                          <button
                            onClick={() => handleAvailability(game.id, "unavailable")}
                            className={`${styles.availabilityButton} ${myAvailability[game.id] === "unavailable" ? styles.unavailableActive : ""}`}
                          >
                            <span className={styles.buttonIcon}>✗</span> Can't Make It
                          </button>
                        </div>
                      )}
                      
                      {/* Manager actions */}
                      {canManageSchedule && (
                        <div className={styles.managerActions}>
                          <button
                            onClick={() => handleEditClick(game)}
                            className={styles.editButton}
                          >
                            <span className={styles.buttonIcon}>✏️</span> Edit Game
                          </button>
                          <button
                            onClick={() => handleRemoveGame(game.id)}
                            className={styles.removeButton}
                          >
                            <span className={styles.buttonIcon}>🗑️</span> Remove
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      )}

      {/* Add/Edit game form */}
      {showForm && (
        <div id="game-form" className={styles.gameForm}>
          <h2 className={styles.formTitle}>
            <span className={styles.formIcon}>ℹ️</span>
            {gameToEdit ? "Edit Game Details" : "Schedule New Game"}
          </h2>
          <ScheduleNewGame
            teamId={teamId}
            onGameSaved={handleGameSaved}
            gameToEdit={gameToEdit}
            onCancelEdit={() => {
              setGameToEdit(null);
              setShowForm(false);
            }}
          />
        </div>
      )}
    </div>
  );
}

export default SchedulePage;